import { ArrowDownLeft, ArrowUpRight, Info } from "lucide-react";
import { useId, useMemo, useState } from "react";

import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaAmountInputControlled } from "@/shared/components/forms/ColocaAmountInput";
import { ColocaCheckbox } from "@/shared/components/forms/ColocaCheckbox";
import { ColocaChoiceCards } from "@/shared/components/forms/ColocaChoiceCards";
import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { trimTrailingZeros } from "@/shared/utils/decimal";

import {
  DEFAULT_COMMISSION_PERCENT,
  QUOTE_DECIMALS,
  quoteRecharge,
  rechargeQuoteIssue,
  type RechargeQuoteMode,
} from "../../utils/rechargeQuote";

const CURRENCY = "USD";
const MICRO_LABEL = "font-mono text-[10px] uppercase tracking-wider text-muted-foreground";

/**
 * The quoter itself: which side is fixed, how much, what the desk charges, and the three figures that follow.
 *
 * Everything here is arithmetic in the browser. No quote is requested, reserved or stored — the operator
 * reads these numbers to the client, and the credit is registered afterwards from this same screen, with its
 * own reference. That is why there is no submit button: there is nothing to submit.
 */
export function RechargeQuoteCalculator({ className }: { className?: string }) {
  const { t } = useTranslations("cardWallets");
  const amountId = useId();
  const commissionId = useId();

  const [mode, setMode] = useState<RechargeQuoteMode>("deposit");
  const [amount, setAmount] = useState("");
  const [commission, setCommission] = useState(DEFAULT_COMMISSION_PERCENT);
  const [commissionUnlocked, setCommissionUnlocked] = useState(false);

  const issue = rechargeQuoteIssue(amount, commission);
  const quote = useMemo(() => quoteRecharge(amount, commission, mode), [amount, commission, mode]);

  // What the chosen mode was actually asked, shown big; the other two figures support it.
  const headline = quote && (mode === "deposit" ? quote.credited : quote.deposit);
  const headlineLabel = mode === "deposit" ? t("quote_result_credited") : t("quote_result_deposit");
  const percentLabel = trimTrailingZeros(commission.trim() || "0");

  return (
    <div className={cn("space-y-5", className)}>
      <section className="space-y-2">
        <h3 className={MICRO_LABEL}>{t("quote_mode_label")}</h3>
        {/* One per row on a phone, side by side from `md` up. The breakpoint is `md` on purpose and not
            `sm`: 768px is the same boundary `ColocaDialog`'s `mobileFullscreen` uses to switch between the
            drawer and the desktop dialog (`MOBILE_BREAKPOINT`), so the grid can never go two-up inside the
            narrow drawer. `cn()` keeps both classes — they live in different variant groups. */}
        <ColocaChoiceCards
          aria-label={t("quote_mode_label")}
          columns={1}
          className="md:grid-cols-2"
          value={mode}
          onValueChange={(next) => setMode(next as RechargeQuoteMode)}
          items={[
            {
              value: "deposit",
              icon: <ArrowUpRight className="size-5" />,
              label: t("quote_mode_deposit_title"),
              description: t("quote_mode_deposit_description"),
            },
            {
              value: "credited",
              icon: <ArrowDownLeft className="size-5" />,
              label: t("quote_mode_credited_title"),
              description: t("quote_mode_credited_description"),
            },
          ]}
        />
      </section>

      <section className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <ColocaLabel htmlFor={amountId}>
            {mode === "deposit" ? t("quote_amount_label_deposit") : t("quote_amount_label_credited")}
          </ColocaLabel>
          <ColocaAmountInputControlled
            id={amountId}
            value={amount}
            onValueChange={setAmount}
            suffix={CURRENCY}
            maxFractionDigits={QUOTE_DECIMALS}
            groupThousands
            placeholder="0,00"
            aria-describedby={`${amountId}-hint`}
          />
          <p id={`${amountId}-hint`} className="text-xs text-muted-foreground">
            {issue === "amount_invalid" ? (
              <span className="text-destructive">{t("quote_issue_amount_invalid")}</span>
            ) : (
              t("quote_amount_hint")
            )}
          </p>
        </div>

        <div className="space-y-2">
          <ColocaLabel htmlFor={commissionId}>{t("quote_commission_label")}</ColocaLabel>
          <ColocaAmountInputControlled
            id={commissionId}
            value={commission}
            onValueChange={setCommission}
            suffix="%"
            maxFractionDigits={QUOTE_DECIMALS}
            disabled={!commissionUnlocked}
            aria-describedby={`${commissionId}-hint`}
          />
          <ColocaCheckbox
            checked={commissionUnlocked}
            onCheckedChange={(checked) => {
              setCommissionUnlocked(checked);
              // Unticking restores the desk's rate, so an edited figure can never survive as a silent
              // default on the next quote.
              if (!checked) setCommission(DEFAULT_COMMISSION_PERCENT);
            }}
            label={t("quote_commission_unlock")}
          />
          <p id={`${commissionId}-hint`} className="text-xs text-muted-foreground">
            {issue === "commission_invalid" ? (
              <span className="text-destructive">{t("quote_issue_commission_invalid")}</span>
            ) : (
              t("quote_commission_hint", { percent: trimTrailingZeros(DEFAULT_COMMISSION_PERCENT) })
            )}
          </p>
        </div>
      </section>

      {quote && headline ? (
        <section
          aria-live="polite"
          className="overflow-hidden rounded-xl border border-primary/25 bg-primary/5"
        >
          <div className="border-b border-primary/20 px-5 py-4">
            <p className={MICRO_LABEL}>{headlineLabel}</p>
            <ColocaAmountDisplay
              size="xl"
              value={headline}
              currency={CURRENCY}
              fractionDigits={QUOTE_DECIMALS}
              className="mt-1.5 block text-foreground"
            />
          </div>

          <dl className="divide-y divide-border/60 px-5 text-sm">
            <QuoteRow
              label={mode === "deposit" ? t("quote_result_pays") : t("quote_result_deposit")}
              value={quote.deposit}
            />
            <QuoteRow
              label={t("quote_result_commission", { percent: percentLabel })}
              value={quote.commission}
            />
            {mode === "credited" && <QuoteRow label={t("quote_result_credited")} value={quote.credited} />}
          </dl>

          {quote.creditedExceedsTarget && (
            <p className="flex items-start gap-2 border-t border-primary/20 px-5 py-3 text-xs text-muted-foreground">
              <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
              <span>
                {t("quote_rounding_note", {
                  amount: `${trimTrailingZeros(quote.credited)} ${CURRENCY}`,
                })}
              </span>
            </p>
          )}
        </section>
      ) : (
        <p className="rounded-xl border border-dashed border-border px-5 py-6 text-center text-sm text-muted-foreground">
          {t("quote_empty")}
        </p>
      )}

      <p className="text-xs leading-relaxed text-muted-foreground">{t("quote_footnote")}</p>
    </div>
  );
}

function QuoteRow({ label, value }: { label: string; value: string }) {
  return (
    // `items-start`: the label wraps on a narrow dialog, and a stretched cell would drop the amount half a
    // line below it (design-system rule 4).
    <div className="flex items-start justify-between gap-4 py-2.5">
      <dt className="min-w-0 text-muted-foreground">{label}</dt>
      <dd className="shrink-0">
        <ColocaAmountDisplay
          size="sm"
          value={value}
          currency={CURRENCY}
          fractionDigits={QUOTE_DECIMALS}
          className="text-foreground"
        />
      </dd>
    </div>
  );
}
